import { Avatar, Box, Chip, Typography } from '@mui/material'
import "@fontsource/rubik";
import { generateDarkColor, getInitials } from '../../../utils/commonServices'
import { formatHistoryDateTime, isNoteEdited } from '../../../utils/revisionHistory'

/**
 * Read-only notes for the selected version. Adding / editing / deleting notes
 * still happens through the live revision panel (`/api/v2/logs`); this view only
 * displays what the history endpoint returned.
 *
 * The caller decides whether to mount this at all - it is hidden entirely when
 * `notes_visible` is false, which is also when the `notes` key is absent.
 */
const RevisionHistoryNotes = (props) => {
  const { revision } = props;
  const notes = Array.isArray(revision?.notes) ? revision.notes : [];

  return (
    <Box textAlign='left'>
      <Typography
        sx={{ fontWeight: 600, fontFamily: 'Rubik', color: 'black', fontSize: { xs: '10px', md: '12px', lg: '13px', xl: '18px' }, mb: 1 }}
      >
        Revision notes
      </Typography>

      {!revision ? (
        <Typography
          data-testid='revision-history-no-revision'
          sx={{ fontFamily: 'Rubik', color: '#08231B', fontSize: { xs: '8px', md: '10px', lg: '10px', xl: '13px' } }}
        >
          No revision requested for this version
        </Typography>
      ) : notes.length === 0 ? (
        <Typography
          data-testid='revision-history-no-notes'
          sx={{ fontFamily: 'Rubik', color: '#08231B', fontSize: { xs: '8px', md: '10px', lg: '10px', xl: '13px' } }}
        >
          No notes were added to this revision
        </Typography>
      ) : (
        notes.map((note) => {
          const authorName = note?.author?.name || '';
          const isGuest = note?.author?.type === 'guest';
          return (
            <Box key={note?.id} data-testid='revision-history-note' className='flex a-start' gap={1.5} sx={{ mb: 1.5 }}>
              <Avatar
                sx={{
                  borderRadius: '5px',
                  backgroundColor: generateDarkColor({ name: authorName }, 'name'),
                  color: 'white',
                  width: { xs: '18px', sm: '20px', md: '22px', lg: '23.5px', xl: '35px' },
                  height: { xs: '18px', sm: '20px', md: '22px', lg: '23.5px', xl: '35px' },
                  fontSize: { xs: '7px', md: '8px', lg: '9px', xl: '12px' },
                  textTransform: 'uppercase',
                  fontFamily: 'Rubik',
                }}
              >
                {getInitials(authorName)}
              </Avatar>

              <Box sx={{ flexGrow: 1 }}>
                <Box className='flex a-center' gap={0.8} sx={{ flexWrap: 'wrap' }}>
                  <Typography
                    sx={{ fontFamily: 'Rubik', fontWeight: 600, color: '#08231B', fontSize: { xs: '8px', md: '10px', lg: '10px', xl: '13px' } }}
                  >
                    {authorName}
                  </Typography>
                  {isGuest && (
                    <Chip
                      label='Guest'
                      size='small'
                      data-testid='revision-history-guest-tag'
                      sx={{ height: { xs: '12px', xl: '18px' }, fontFamily: 'Rubik', fontSize: { xs: '6px', md: '7px', xl: '10px' } }}
                    />
                  )}
                  {isNoteEdited(note) && (
                    <Typography
                      data-testid='revision-history-note-edited'
                      sx={{ fontFamily: 'Rubik', fontStyle: 'italic', opacity: 0.65, color: '#08231B', fontSize: { xs: '7px', md: '9px', xl: '12px' } }}
                    >
                      edited{note?.updated_by?.name ? ` by ${note.updated_by.name}` : ''}
                    </Typography>
                  )}
                </Box>

                <Typography
                  sx={{ fontFamily: 'Rubik', color: '#08231B', textAlign: 'justify', wordBreak: 'break-word', fontSize: { xs: '8px', md: '10px', lg: '10px', xl: '13px' } }}
                >
                  {note?.message}
                </Typography>

                <Typography
                  sx={{ fontFamily: 'Rubik', fontWeight: 600, fontStyle: 'italic', color: '#08231B', fontSize: { xs: '7px', md: '9px', lg: '9px', xl: '12px' }, mt: 0.3 }}
                >
                  {note?.timestamp}
                  {note?.created_at ? ` · ${formatHistoryDateTime(note.created_at)}` : ''}
                </Typography>
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  )
}

export default RevisionHistoryNotes
